import React, { useState } from 'react';
import { MdExpandMore, MdExpandLess, MdCheck, MdClose, MdHistory } from 'react-icons/md';
import style from './AdaptationBanner.module.css';

const TRIGGER_META = {
	consistency_streak: {
		icon: '🔥',
		label: 'Consistency Detected',
		color: '#4CAF50',
	},
	faltering: {
		icon: '💪',
		label: 'Getting You Back On Track',
		color: '#FF9800',
	},
	plateau: {
		icon: '📈',
		label: 'Breaking Your Plateau',
		color: '#2196F3',
	},
	rapid_completion: {
		icon: '⚡',
		label: 'You\'re Ready For More',
		color: '#9C27B0',
	},
	level_up: {
		icon: '🏆',
		label: 'Level Up',
		color: '#FFD700',
	},
	first_week_gentle: {
		icon: '🌱',
		label: 'Easing You In',
		color: '#66BB6A',
	},
};

const AdaptationBanner = ({ adaptation, onApply, onDismiss, isApplying }) => {
	const [expanded, setExpanded] = useState(false);

	if (!adaptation) return null;

	const meta = TRIGGER_META[adaptation.trigger] || {
		icon: '✨',
		label: 'Plan Update',
		color: '#4CAF50',
	};

	const changes = adaptation.changes || {};
	const hasChanges =
		(changes.addedWorkouts?.length > 0) ||
		(changes.removedWorkouts?.length > 0) ||
		(changes.modifiedWorkouts?.length > 0);

	return (
		<div className={style.Banner} style={{ '--accent': meta.color }}>
			<div className={style.BannerHeader}>
				<div className={style.BannerLeft}>
					<span className={style.TriggerIcon}>{meta.icon}</span>
					<div className={style.BannerTextGroup}>
						<span className={style.TriggerLabel}>{meta.label}</span>
						<span className={style.Summary}>{adaptation.summary}</span>
					</div>
				</div>
				<button
					className={style.ExpandToggle}
					onClick={() => setExpanded(!expanded)}
					aria-label={expanded ? 'Collapse changes' : 'View changes'}
				>
					{expanded ? <MdExpandLess /> : <MdExpandMore />}
				</button>
			</div>

			{expanded && hasChanges && (
				<div className={style.ChangesPanel}>
					{changes.addedWorkouts?.length > 0 && (
						<div className={style.ChangeGroup}>
							<span className={style.ChangeGroupLabel}>Adding</span>
							{changes.addedWorkouts.map((w, i) => (
								<div key={i} className={style.ChangeItem}>
									<span className={style.ChangeAdd}>+</span>
									<span className={style.ChangeName}>{w.workoutType}</span>
									<span className={style.ChangeMeta}>{w.workoutSets} sets · {w.workoutReps} reps</span>
								</div>
							))}
						</div>
					)}

					{changes.modifiedWorkouts?.length > 0 && (
						<div className={style.ChangeGroup}>
							<span className={style.ChangeGroupLabel}>Modifying</span>
							{changes.modifiedWorkouts.map((w, i) => (
								<div key={i} className={style.ChangeItem}>
									<span className={style.ChangeMod}>~</span>
									<span className={style.ChangeName}>{w.workoutType}</span>
									<span className={style.ChangeMeta}>{w.workoutSets} sets · {w.workoutReps} reps</span>
								</div>
							))}
						</div>
					)}

					{changes.removedWorkouts?.length > 0 && (
						<div className={style.ChangeGroup}>
							<span className={style.ChangeGroupLabel}>Removing</span>
							{changes.removedWorkouts.map((name, i) => (
								<div key={i} className={style.ChangeItem}>
									<span className={style.ChangeRemove}>−</span>
									<span className={style.ChangeName}>{name}</span>
								</div>
							))}
						</div>
					)}

					{changes.note && (
						<p className={style.ChangeNote}>{changes.note}</p>
					)}
				</div>
			)}

			<div className={style.BannerActions}>
				<button
					className={style.ApplyBtn}
					onClick={onApply}
					disabled={isApplying}
				>
					{isApplying ? (
						<span className={style.ApplySpinner} />
					) : (
						<MdCheck />
					)}
					<span>{isApplying ? 'Applying...' : 'Apply Changes'}</span>
				</button>
				<button className={style.DismissBtn} onClick={onDismiss}>
					<MdClose />
					<span>Not Now</span>
				</button>
			</div>
		</div>
	);
};

const AdaptationHistoryItem = ({ event }) => {
	const meta = TRIGGER_META[event.trigger] || { icon: '✨', label: 'Update' };
	const date = new Date(event.appliedAt || event.firedAt);
	const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

	return (
		<div className={style.HistoryItem}>
			<span className={style.HistoryIcon}>{meta.icon}</span>
			<div className={style.HistoryText}>
				<span className={style.HistoryLabel}>{meta.label}</span>
				<span className={style.HistorySummary}>{event.summary}</span>
			</div>
			<div className={style.HistoryMeta}>
				<span className={style.HistoryDate}>{dateStr}</span>
				{event.appliedAt && <span className={style.HistoryApplied}>Applied</span>}
				{event.dismissed && <span className={style.HistoryDismissed}>Skipped</span>}
			</div>
		</div>
	);
};

const AdaptationHistory = ({ history }) => {
	const [isOpen, setIsOpen] = useState(false);

	if (!history || history.length === 0) return null;

	return (
		<div className={style.HistorySection}>
			<button className={style.HistoryToggle} onClick={() => setIsOpen(!isOpen)}>
				<MdHistory />
				<span>Plan Evolution History ({history.length})</span>
				{isOpen ? <MdExpandLess /> : <MdExpandMore />}
			</button>
			{isOpen && (
				<div className={style.HistoryList}>
					{history.slice(0, 10).map((event, i) => (
						<AdaptationHistoryItem key={event._id || i} event={event} />
					))}
				</div>
			)}
		</div>
	);
};

export { AdaptationBanner, AdaptationHistory };
export default AdaptationBanner;
